/**
 * Demo Time Step — date, time, and bay selection for a demo booking.
 *
 * Mirrors `BookingTimeStep`'s page structure exactly (both responsive
 * branches, same module CSS) through the shared `useAvailabilitySelection`
 * core, minus everything a demo doesn't have: the duration renders as a
 * static label (the length is club config, not a user option), no guest
 * selector, no pricing, no
 * subscription machinery. Demo wording throughout.
 *
 * CTA fork (D26 + product):
 * - authed + enrolled  → "Confirm Demo Booking": create immediately (the
 *   membership-covered standard path's shape) → /demo/confirmed/:id.
 * - authed + NOT enrolled → the standard flow's exact block: browsable page,
 *   disabled "No access" CTA + contact-club line. No auto-enrollment.
 * - un-authed → "Continue to Confirmation": advance to the finish step, where
 *   the account is created (never an incomplete user).
 *
 * @module pages/demo/steps/DemoTimeStep
 */

import { useCallback } from 'react';
import { parseISO } from 'date-fns';
import { DatePicker } from '@butter/ui/components/inputs';
import { TimeSlotSelector } from '../../booking/components/TimeSlotSelector';
import { BaySelector, BookingError } from '@butter/ui/domains/booking';
import { PencilIcon, ArrowIcon } from '@butter/ui/icons';
import { useCreateDemoBooking } from '~/services/internalApi/booking';
import { useClubAccess } from '~/services/internalApi/club';
import { butterToast } from '@butter/ui/patterns';
import { useWaiverSubmission } from '../../booking/components/useWaiverSubmission';
import { waiverRefusalMessage } from '../../booking/components/waiverCompletion';
import { addBreadcrumb, captureException, captureMessage } from '@butter/monitoring';
import { formatTime } from '@butter/library/datetime';
import { useVisualStep, VISUAL_STEPS } from '~/services/internalApi/booking/context';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { useTenantNavigate } from '~/lib/navigation';
import { useAvailabilitySelection, type AvailabilityPhase } from '../../booking/shared/useAvailabilitySelection';
import { DemoTicketCard } from '../components/DemoTicketCard';
import styles from '../../booking/steps/BookingTimeStep.module.css';

export interface DemoTimeStepProps {
  /** Un-authed CTA: advance to the account-creation finish step. */
  onAdvanceToFinish: () => void;
}

export function DemoTimeStep({ onAdvanceToFinish }: DemoTimeStepProps) {
  const monitoringCallbacks = {
    onCaptureException: captureException,
    onCaptureMessage: captureMessage,
  } as const;

  const navigateWithParams = useTenantNavigate();

  const { setVisualStep } = useVisualStep();

  // The demo length is club config — read through the same cached query the
  // selection hook uses, but ahead of it so the fixed duration can be passed
  // in. DemoFlowPage gates on demoBookingsEnabled; the config column is NOT
  // NULL by design (S1).
  const { data: clubForConfig } = useClubQuery();
  const demoDurationMinutes = clubForConfig?.demoBookingConfig?.sessionDurationMinutes;

  // Same phase → visual-step mapping as the standard flow; no logical-step
  // gate needed — this flow's step 0 is the only step that mounts this.
  const onPhaseChange = useCallback((phase: AvailabilityPhase) => {
    setVisualStep(phase === 'time' ? VISUAL_STEPS.BOOKING_TIME : VISUAL_STEPS.BAY_SELECTION);
  }, [setVisualStep]);

  const {
    club,
    isAuthenticated,
    authUser,
    numericUserId,
    isMobile,
    mobileSubstep,
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedBayId,
    hasTimeSelection,
    selectedSlots,
    selectedRange,
    bayFilterId,
    clearBooking,
    effectiveSelectedDuration,
    gridSlots,
    availabilityLoading,
    availabilityIsStale,
    availabilityError,
    availableBays,
    baysLoading,
    baysError,
    selectedBay,
    isMember,
    baysForSelector,
    startTimeISO,
    endTimeISO,
    getMinDate,
    getMaxDate,
    handleDateChange,
    handleSlotsSelect,
    handleBayChange,
    handleBaySelect,
    handleDurationChange,
    handleSelectBayClick,
    handleEditTimeClick,
  } = useAvailabilitySelection({
    flow: 'demo',
    fixedDurationMinutes: demoDurationMinutes,
    onPhaseChange,
  });

  // Authed-but-not-enrolled block — the standard flow's exact behavior.
  const { status: clubAccessStatus } = useClubAccess();
  const blocked = clubAccessStatus === 'no-access';

  const createDemoBooking = useCreateDemoBooking({
    onSuccess: (result) => {
      const { booking, accessControl, access } = result;
      addBreadcrumb('booking', 'Demo booking created', {
        bookingId: booking.id,
        bayId: booking.bayId,
      }, 'info');

      clearBooking();

      // useTenantNavigate carries tenant-handle + club-id automatically.
      navigateWithParams(`/demo/confirmed/${booking.id}`, {
        state: { booking, accessControl, access },
      });
    },
    onError: (error) => {
      butterToast.error(error.message || 'Failed to book your demo. Please try again.');
      captureException(error, {
        level: 'error',
        tags: { operation: 'create_demo_booking' },
        extra: { selectedBayId, selectedDate, selectedStartTime, selectedEndTime },
      });
    },
  });
  // The mutation's own lifecycle drives the busy state — no hand-managed
  // boolean whose reset-on-error-only invariant a reader must reconstruct.
  const isCreating = createDemoBooking.isPending;

  const canAct =
    selectedStartTime !== null &&
    selectedEndTime !== null &&
    selectedBay?.isAvailable === true;

  // Same as BookingTimeStep: this path creates a booking with no checkout, so
  // the entry gate is otherwise the only thing that has looked.
  const waiverGate = useWaiverSubmission(clubForConfig?.id);

  const handleConfirmDemo = () => {
    if (!canAct || !club || !numericUserId || !startTimeISO || !endTimeISO || !selectedBayId) {
      console.warn('[DemoTimeStep] Cannot confirm - missing required selections');
      return;
    }
    // No checkout on this path, so nothing was captured and `satisfied` reduces
    // to "the server says this member owes nothing". Same hook the checkout steps
    // use — one question, one spelling. The entry gate is the only other thing
    // that looks, and it fails OPEN; there is no server-side check.
    if (!waiverGate.satisfied) {
      butterToast.error(waiverRefusalMessage(waiverGate.answerMissing));
      // Only when the answer is missing. `retry()` forces a real request even on
      // a settled query, so firing it on the "you haven't signed" path spends a
      // round trip re-asking a question already answered correctly.
      if (waiverGate.answerMissing) void waiverGate.retry();
      return;
    }

    addBreadcrumb('booking', 'Confirm demo clicked', {
      bayId: selectedBayId,
      date: selectedDate,
      startTime: selectedStartTime,
      endTime: selectedEndTime,
    });
    createDemoBooking.mutate({
      clubId: club.id,
      bayId: selectedBayId,
      userId: numericUserId,
      startTime: startTimeISO,
      endTime: endTimeISO,
    });
  };

  const handleContinueToFinish = () => {
    if (!canAct) return;
    addBreadcrumb('booking', 'Continue to demo confirmation clicked', {
      bayId: selectedBayId,
      date: selectedDate,
    });
    setVisualStep(VISUAL_STEPS.CONFIRMATION);
    onAdvanceToFinish();
  };

  const ticketMode = isAuthenticated && authUser ? 'confirm' as const : 'continue' as const;
  const handleAction = ticketMode === 'confirm' ? handleConfirmDemo : handleContinueToFinish;

  if (!club) {
    return null;
  }

  if (!club.bays || club.bays.length === 0) {
    return (
      <BookingError
        {...monitoringCallbacks}
        title="No bays available"
        message="This club has no active bays configured. Please contact support."
        context={{
          category: 'club_configuration',
          type: 'no_bays',
          severity: 'error',
          clubId: club.id,
        }}
      />
    );
  }

  if (availabilityError) {
    return (
      <BookingError
        {...monitoringCallbacks}
        title="Unable to load availability"
        message="We couldn't load demo availability for the selected date. Please try again."
        error={availabilityError}
        context={{
          category: 'api_error',
          type: 'availability_fetch_failed',
          severity: 'error',
          clubId: club.id,
          extra: { errorMessage: availabilityError.message, date: selectedDate },
        }}
      />
    );
  }

  return (
    <div className="">
      <h2
        className="mb-6 text-xl font-semibold text-gray-900"
        data-testid="booking-header-day-select"
      >
        {mobileSubstep === 'time'
          ? 'Select a date and an available time'
          : 'Choose a demo bay'}
      </h2>

      {/* Date Picker - Desktop: always visible, Mobile: only on time substep */}
      {(!isMobile || mobileSubstep === 'time') && (
        <DatePicker
          floatingCalendar
          selectedDate={parseISO(selectedDate)}
          onDateSelect={handleDateChange}
          minDate={getMinDate()}
          maxDate={getMaxDate()}
        />
      )}

      {/* Time Slot Selector - demo wording, no duration dropdown (fixed length) */}
      {(!isMobile || mobileSubstep === 'time') && (
        <TimeSlotSelector
          graceMinutes={club?.bookingGraceMinutes}
          title="Available demo time slots"
          durationDisplay="label"
          slots={gridSlots}
          // First load + a date/bay change in flight both show the grid skeleton.
          isLoading={availabilityLoading || availabilityIsStale}
          bays={baysForSelector}
          durationOptions={demoDurationMinutes ? [demoDurationMinutes] : []}
          selectedBayId={bayFilterId || 'all'}
          selectedDuration={effectiveSelectedDuration}
          selectedSlots={selectedSlots}
          selectedRange={selectedRange}
          isMember={isMember}
          onBayChange={handleBayChange}
          onDurationChange={handleDurationChange}
          onSlotsSelect={handleSlotsSelect}
        />
      )}

      {/* Mobile: "Select a Bay" Button */}
      {isMobile && mobileSubstep === 'time' && hasTimeSelection && (
        <button
          onClick={handleSelectBayClick}
          className={styles.selectBayButton}
          aria-label="Continue to bay selection"
          data-testid="booking-continue-to-bay-button"
        >
          <span>Select a Bay</span>
          <span className={styles.selectBayButtonIcon} aria-hidden="true">
            <ArrowIcon className="size-5" />
          </span>
        </button>
      )}

      {/* Mobile: Bay Substep View */}
      {isMobile && mobileSubstep === 'bay' && (
        <div className={styles.mobileBaySubstep}>
          <div className={styles.editTimeChip}>
            <span className={styles.editTimeChipTime}>
              {selectedStartTime && selectedEndTime &&
                `${formatTime(selectedStartTime)} - ${formatTime(selectedEndTime)}`
              }
            </span>
            <button
              onClick={handleEditTimeClick}
              className={styles.editTimeChipButton}
              aria-label="Edit selected time"
            >
              <PencilIcon className="size-5" />
            </button>
          </div>

          <BaySelector
            title="Choose a demo bay"
            bays={availableBays?.items || []}
            selectedBayId={selectedBay?.id || null}
            onBaySelect={handleBaySelect}
            isLoading={baysLoading}
            error={baysError?.message}
          />

          {selectedBay?.isAvailable && (
            <DemoTicketCard
              mode={ticketMode}
              blocked={blocked}
              onAction={handleAction}
              canAct={canAct}
              isCreating={isCreating}
              floatingMobileCta
              className="mt-12"
            />
          )}
        </div>
      )}

      {/* Desktop: Bay Selection (Progressive Disclosure) */}
      <div
        className={styles.baySection}
        data-visible={hasTimeSelection ? 'true' : 'false'}
        aria-hidden={!hasTimeSelection}
      >
        <BaySelector
          title="Choose a demo bay"
          bays={availableBays?.items || []}
          selectedBayId={selectedBay?.id || null}
          onBaySelect={handleBaySelect}
          isLoading={baysLoading}
          error={baysError?.message}
        />
      </div>

      {/* Desktop: Demo Summary (Progressive Disclosure) */}
      <div
        className={styles.summarySection}
        data-visible={selectedBay?.isAvailable ? 'true' : 'false'}
        aria-hidden={!selectedBay?.isAvailable}
      >
        <DemoTicketCard
          mode={ticketMode}
          blocked={blocked}
          onAction={handleAction}
          canAct={canAct}
          isCreating={isCreating}
          className="mt-8"
        />
      </div>
    </div>
  );
}
